import * as React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Brain, CalendarCheck, ChartLine, ChevronLeft, ChevronRight, Clock, FolderKanbanIcon, GraduationCap, House, ListTodo, Map, Menu, NotebookPen, Rss, Target, X} from 'lucide-react';

export default function Sidebar() {
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [isMobileOpen, setIsMobileOpen] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);

    React.useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setIsCollapsed(true);
                setIsMobileOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navGroups = [
        {
            label: null,
            items: [
                {to: '/home', label: 'Home', icon: House},
            ],
        },
        {
            label: 'Organize',
            items: [
                {to: '/planner', label: 'Planner', icon: CalendarCheck},
                {to: '/projects', label: 'Projects', icon: FolderKanbanIcon},
                {to: '/task', label: 'Tasks', icon: ListTodo},
            ],
        },
        {
            label: 'Focus',
            items: [
                {to: '/session', label: 'Session', icon: Clock},
                {to: '/trackers', label: 'Trackers', icon: Target},
            ],
        },
        {
            label: 'Remember',
            items: [
                {to: '/notes', label: 'Notes', icon: NotebookPen},
                {to: '/recall', label: 'Flashcards', icon: Brain},
            ],
        },
        {
            label: 'Learn',
            items: [
                {to: '/roadmaps', label: 'Roadmaps', icon: Map},
                {to: '/knowledge-feed', label: 'Feed', icon: Rss},
                {to: '/practice', label: 'Practice', icon: GraduationCap},
            ],
        },
        {
            label: 'Insights',
            items: [
                {to: '/reports', label: 'Reports', icon: ChartLine},
            ],
        },
    ];

    const desktopWidthClass = isCollapsed ? 'w-[76px]' : 'w-[228px]';
    const showDrawer = isMobile ? isMobileOpen : true;

    return (
        <>
            {isMobile && !isMobileOpen && (
                <button
                    type="button"
                    onClick={() => setIsMobileOpen(true)}
                    className="fixed left-4 top-[76px] z-40 rounded-xl border border-[var(--border-color)] bg-[var(--header-bg)] p-2 text-[var(--text-primary)] shadow-lg"
                    aria-label="Open sidebar"
                >
                    <Menu size={20}/>
                </button>
            )}

            {isMobile && isMobileOpen && (
                <button
                    type="button"
                    onClick={() => setIsMobileOpen(false)}
                    className="fixed inset-0 z-30 bg-black/50"
                    aria-label="Close sidebar overlay"
                />
            )}

            <aside
                className={[
                    'z-40 transition-all duration-300 ease-out',
                    isMobile ? 'fixed left-0 top-[64px] h-[calc(100vh-64px)]' : 'relative',
                    desktopWidthClass,
                    showDrawer ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0',
                ].join(' ')}
            >
                <div className="m-0 h-full rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-3 shadow-[var(--menu-shadow)] md:ml-1">
                    <div className="mb-2 flex items-center justify-between border-b border-[var(--border-color)] pb-3">
                        {!isCollapsed && <span className="pl-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">MY SPACE</span>}

                        <button
                            type="button"
                            onClick={() => {
                                if (isMobile) {
                                    setIsMobileOpen(false);
                                    return;
                                }
                                setIsCollapsed((prev) => !prev);
                            }}
                            className="rounded-lg border border-[var(--border-color)] p-1.5 text-[var(--text-secondary)] transition-colors hover:border-[var(--brand-primary)]/50 hover:bg-[var(--surface-hover)] hover:text-[var(--brand-primary)]"
                            aria-label={isMobile ? 'Close sidebar' : (isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
                        >
                            {isMobile ? <X size={16}/> : (isCollapsed ? <ChevronRight size={16}/> : <ChevronLeft size={16}/>)}
                        </button>
                    </div>

                    <nav className="flex flex-col" role="navigation" aria-label="Main navigation">
                        {navGroups.map((group, groupIndex) => (
                            <div
                                key={group.label ?? 'home'}
                                className={groupIndex === 0 ? '' : (isCollapsed ? 'mt-2 border-t border-[var(--border-color)] pt-2' : 'mt-4')}
                            >
                                {group.label && !isCollapsed && (
                                    <div className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">
                                        {group.label}
                                    </div>
                                )}

                                <div className="flex flex-col gap-1">
                                    {group.items.map((item) => {
                                        const Icon = item.icon;
                                        const isActive = location.pathname === item.to || (item.to === '/roadmaps' && location.pathname.startsWith('/roadmap/'));

                                        return (
                                            <Link
                                                key={item.to}
                                                to={item.to}
                                                onClick={() => isMobile && setIsMobileOpen(false)}
                                                title={isCollapsed ? item.label : undefined}
                                                className={[
                                                    'group flex items-center rounded-lg border px-3 py-2 text-sm transition-colors duration-200',
                                                    isCollapsed ? 'justify-center' : 'justify-start gap-2.5',
                                                    isActive
                                                        ? 'border-[var(--brand-primary)]/30 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]'
                                                        : 'border-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
                                                ].join(' ')}
                                            >
                                                <Icon size={20} className={isActive ? 'text-[var(--brand-primary)]' : 'text-[var(--text-muted)] group-hover:text-[var(--text-primary)]'}/>
                                                {!isCollapsed && <span className="font-medium">{item.label}</span>}
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>
                </div>
            </aside>
        </>
    );
}
